// PRELIEVO, LATO CLIENT
//
// Il contrario del deposito, e non solo nel verso dei soldi: nel deposito e' il
// wallet dell'utente a firmare una transazione vera; qui l'utente firma soltanto
// un'AUTORIZZAZIONE off-chain (gratis, nessuna transazione), e la transazione la
// costruisce e firma il server con la chiave della tesoreria.
//
// La conseguenza pratica e' importante e vale la pena saperla: una firma di
// prelievo rubata, da sola, non muove niente in catena. Puo' solo autorizzare un
// pagamento verso il wallet dell'utente stesso — perche' la destinazione non e'
// un campo che questo file possa scegliere. Il server paga al wallet della
// sessione e a nessun altro.
//
// Due passi:
//   1. `/api/withdraw/challenge` → il testo da far leggere e firmare in Phantom;
//   2. `/api/withdraw` con la sola firma → il server verifica, scala il saldo
//      interno e mette la transazione in coda.
//
// Non si riprova da soli come nel deposito: li' insistere era sicuro (accredito
// idempotente sulla signature), qui una nuova richiesta e' un NUOVO prelievo.
// Se l'esito e' incerto si guarda `/api/withdrawals`, che e' la fonte di verita'.

import bs58 from 'bs58';
import { authHeader } from './authClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export async function fetchWithdrawConfig() {
  const res = await fetch(`${API_BASE}/api/withdraw/config`);
  if (!res.ok) throw new Error('Configurazione dei prelievi non disponibile');
  return res.json();
}

// Storico dei propri prelievi, con la signature da cercare su un explorer.
export async function fetchWithdrawals(wallet) {
  const res = await fetch(`${API_BASE}/api/withdrawals`, { headers: { ...authHeader(wallet) } });
  if (!res.ok) throw new Error('Storico prelievi non disponibile');
  return (await res.json()).withdrawals;
}

async function leggiErrore(res) {
  const dati = await res.json().catch(() => ({}));
  const err = new Error(dati.error || `Prelievo rifiutato (HTTP ${res.status})`);
  err.code = dati.code;
  throw err;
}

/**
 * Esegue il prelievo completo.
 *
 * @param {object} opts
 * @param {string} opts.wallet        wallet connesso (e destinazione: la sceglie il server)
 * @param {function} opts.signMessage dal wallet adapter
 * @param {number} opts.sol           quanti SOL prelevare
 * @param {function} [opts.onProgress] messaggi di stato da mostrare
 */
export async function preleva({ wallet, signMessage, sol, onProgress = () => {} }) {
  if (typeof signMessage !== 'function') {
    throw new Error('Questo wallet non supporta la firma di messaggi');
  }

  // 1. Autorizzazione. Il server risponde no subito — importo sotto il minimo,
  // saldo insufficiente, tetto giornaliero, cooldown — invece di farci firmare
  // qualcosa che verrebbe rifiutato dopo.
  const chRes = await fetch(`${API_BASE}/api/withdraw/challenge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader(wallet) },
    body: JSON.stringify({ amount: sol }),
  });
  if (!chRes.ok) await leggiErrore(chRes);
  const sfida = await chRes.json();

  onProgress('Read and sign the authorization in your wallet…');

  // 2. Firma. Il messaggio e' scritto per essere letto: se quello che compare in
  // Phantom non e' il prelievo che l'utente ha chiesto, deve poterlo rifiutare.
  const firma = await signMessage(new TextEncoder().encode(sfida.message));

  onProgress('Authorization signed. Sending the request…');

  // 3. Richiesta. Solo la firma: importo e destinazione sono gia' dentro il
  // testo firmato, e mandarli qui verrebbe rifiutato apertamente dal server.
  const res = await fetch(`${API_BASE}/api/withdraw`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader(wallet) },
    body: JSON.stringify({ signature: bs58.encode(firma) }),
  });
  if (!res.ok) await leggiErrore(res);

  // 202 = trattenuto per revisione manuale (importo sopra soglia). Non e' un
  // errore, ed e' importante non presentarlo come tale.
  return { ...(await res.json()), review: res.status === 202 };
}
